import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { motion } from "framer-motion";
import { toast } from "sonner";

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];
const HEARD_OPTIONS = ["Instagram", "TikTok", "Friend", "Event / pop-up", "Search", "Other"];

export default function Checkout() {
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();
  const { items, subtotal, priceOf, clear } = useCart();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", state: "", city: "",
    address: "", how_heard: "", notes: "",
  });

  if (!user) {
    return (
      <div className="pt-32 px-6 md:px-12 lg:px-24" data-testid="checkout-auth-required">
        <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tighter">Sign in to checkout</h1>
        <p className="text-gray-400 mt-4 max-w-xl">
          Cash on delivery requires an account so we can confirm your delivery details.
        </p>
        <button
          onClick={openAuth}
          data-testid="checkout-signin-btn"
          className="mt-8 bg-white text-black font-display font-bold uppercase tracking-wider px-6 py-3 rounded-sm hover:bg-gray-200 transition-colors"
        >
          Sign in / Create account
        </button>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="pt-32 px-6 md:px-12 lg:px-24 min-h-[60vh]">
        <h1 className="font-display text-4xl uppercase tracking-tighter">Empty bag</h1>
        <p className="text-gray-400 mt-3">Add something before checking out.</p>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product.id,
          quantity: i.quantity,
          batch_mode: i.batch_mode,
          package_key: i.package_key || null,
        })),
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        state: form.state,
        city: form.city.trim(),
        address: form.address.trim(),
        how_heard: form.how_heard,
        notes: form.notes?.trim() || null,
      };
      const { data } = await api.post("/orders/create", payload);
      clear();
      navigate(`/order/success?n=${encodeURIComponent(data.order_number)}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't place order");
    }
    setBusy(false);
  };

  return (
    <div className="pt-24 md:pt-28 px-6 md:px-12 lg:px-24 pb-24" data-testid="checkout-page">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-12">
        <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)] mb-4">/ CHECKOUT</p>
        <h1 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter">
          Cash <span className="italic text-[var(--hk-purple-text)]">on delivery</span>
        </h1>
        <p className="text-gray-400 mt-3 max-w-2xl">
          We deliver. You pay in cash at the door. Fill out the details below and we&apos;ll confirm.
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-10">
        <form onSubmit={onSubmit} className="lg:col-span-2 space-y-8" data-testid="checkout-form">
          <Section title="Delivery details">
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Full name" required value={form.full_name}
                     onChange={(v) => setForm({ ...form, full_name: v })} testid="checkout-name" />
              <Field label="Phone" required type="tel" value={form.phone}
                     onChange={(v) => setForm({ ...form, phone: v })} testid="checkout-phone" />
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">State *</label>
                <select
                  required
                  data-testid="checkout-state"
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2 text-white"
                >
                  <option value="" className="bg-black">Select state</option>
                  {STATES.map((s) => (
                    <option key={s} value={s} className="bg-black">{s}</option>
                  ))}
                </select>
              </div>
              <Field label="City" required value={form.city}
                     onChange={(v) => setForm({ ...form, city: v })} testid="checkout-city" />
              <div className="md:col-span-2">
                <Field label="Street address" required value={form.address}
                       onChange={(v) => setForm({ ...form, address: v })} testid="checkout-address" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">How did you hear about us? *</label>
                <select
                  required
                  data-testid="checkout-how-heard"
                  value={form.how_heard}
                  onChange={(e) => setForm({ ...form, how_heard: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2 text-white"
                >
                  <option value="" className="bg-black">Select one</option>
                  {HEARD_OPTIONS.map((h) => (
                    <option key={h} value={h} className="bg-black">{h}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Delivery notes</label>
                <textarea
                  data-testid="checkout-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-transparent border border-white/15 focus:border-[var(--hk-purple)] outline-none p-3 text-white placeholder-gray-600"
                  placeholder="Apt #, gate code, drop-off instructions..."
                />
              </div>
            </div>
          </Section>

          <Section title="Payment">
            <div className="border border-[var(--hk-orange)]/40 bg-[var(--hk-orange)]/5 p-5">
              <p className="font-display uppercase text-[var(--hk-orange)] tracking-wider">Cash on delivery</p>
              <p className="text-sm text-gray-400 mt-2">
                Pay in cash when your order arrives. No card needed. We&apos;ll text you to confirm before we head out.
              </p>
            </div>
          </Section>

          <button
            type="submit"
            disabled={busy}
            data-testid="place-order-btn"
            className="w-full bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider py-4 rounded-sm hover:bg-[var(--hk-orange-hover)] transition-colors disabled:opacity-60"
          >
            {busy ? "Placing order..." : "Place order — cash on delivery"}
          </button>
        </form>

        <aside className="bg-[var(--hk-surface)] border border-white/10 p-6 h-fit lg:sticky lg:top-32">
          <h3 className="font-display text-xl uppercase tracking-tight mb-5">Your bag</h3>
          <div className="space-y-4 mb-5 max-h-80 overflow-y-auto">
            {items.map((i) => (
              <div key={`${i.product.id}-${i.batch_mode}-${i.package_key || "_"}`} className="flex gap-3 text-sm">
                <img src={resolveImageUrl(i.product.image)} alt="" className="w-14 h-14 object-cover border border-white/10" />
                <div className="flex-1 min-w-0">
                  <p className="font-display uppercase truncate">{i.product.name}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">
                    {i.batch_mode === "wholesale" ? "Wholesale" : "Small batch"}
                    {i.package_label ? ` · ${i.package_label}` : ""} · x{i.quantity}
                  </p>
                </div>
                <span className="text-[var(--hk-orange)] font-display">
                  ${(priceOf(i) * i.quantity).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-4 flex justify-between">
            <span className="text-xs text-gray-500 uppercase tracking-widest">Subtotal</span>
            <span className="font-display text-2xl text-white">${subtotal.toFixed(2)}</span>
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-3">
            Free local delivery · Pay in cash
          </p>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl uppercase tracking-tight mb-5">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">
        {label}{required ? " *" : ""}
      </label>
      <input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-white/20 focus:border-[var(--hk-purple)] outline-none py-2 text-white placeholder-gray-600"
      />
    </div>
  );
}
